import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

type SearchSymbolParams = {
  symbol: string;
  lang: Lang;
};

export const symbolDataSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  market: z.string(),
});

const searchSymbolSchema = z.object({
  data: z.array(symbolDataSchema),
  message: z.string(),
});

export type SymbolData = z.infer<typeof symbolDataSchema>;

export const searchSymbol = async ({
  symbol,
  lang,
}: SearchSymbolParams): Promise<SymbolData[]> => {
  const endpoint = `/search/symbol/${symbol}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return searchSymbolSchema.parse(await res.json()).data;
};

export const useSearchSymbol = (query: string) => {
  const { lang } = useLang();
  return useQuery(
    ["search-symbol", query, lang],
    () => searchSymbol({ symbol: query, lang }),
    {
      enabled: !!query && query.trim().length > 0,
      keepPreviousData: query !== "",
    }
  );
};
